import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

let nextFileUploadId = 0;

/**
 * Design-system file upload field with floating label, drag-and-drop, and
 * optional selected-file chip. Uses native file APIs (no ng2-file-upload).
 *
 * @example
 * <lib-anc-file-upload
 *   [label]="'Attach Invoice'"
 *   [accept]="'.pdf,.png,.jpg'"
 *   [selectedFile]="fileToUpload"
 *   [disabled]="isDisabled"
 *   (fileSelected)="onInvoiceSelected($event)"
 *   (cleared)="onInvoiceCleared()"
 * ></lib-anc-file-upload>
 */
@Component({
    selector: 'lib-anc-file-upload',
    imports: [CommonModule, TranslateModule],
    templateUrl: './anc-file-upload.component.html',
    styleUrls: ['./anc-file-upload.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'anc-file-upload-host',
        '(focusin)': 'onFocusIn()',
        '(focusout)': 'onFocusOut($event)'
    }
})
export class AncFileUploadComponent {
    private readonly host = inject(ElementRef<HTMLElement>);
    private readonly fileInput =
        viewChild<ElementRef<HTMLInputElement>>('fileInput');

    /** Field label (run through the translate pipe). */
    label = input<string>('');
    /** Renders the required asterisk when true. */
    required = input<boolean>(false);
    /** Native `accept` attribute for the file input. */
    accept = input<string>('');
    /** When true, allows selecting multiple files in one browse/drop action. */
    multiple = input<boolean>(false);
    /** Disables browse, drop, and clear interactions. */
    disabled = input<boolean>(false);
    /** Browse button label (run through the translate pipe). */
    browseLabel = input<string>('Browse');
    /** Currently selected file — drives the chip and floated label state. */
    selectedFile = input<File | { name: string } | null>(null);
    /** Optional override for the native input `id` (auto-generated when omitted). */
    inputId = input<string>('');

    /** Emits the chosen file from browse or drag-and-drop. */
    readonly fileSelected = output<File>();
    /** Emits when drag enters/leaves the drop zone. */
    readonly fileOver = output<boolean>();
    /** Emits when the user clears the selected file chip. */
    readonly cleared = output<void>();

    protected readonly focused = signal(false);
    protected readonly dragOver = signal(false);

    protected readonly resolvedInputId = computed(
        () => this.inputId() || this.defaultInputId
    );

    private readonly defaultInputId = `anc-file-upload-${nextFileUploadId++}`;

    protected readonly selectedFileName = computed(() => {
        const file = this.selectedFile();
        return file?.name?.trim() ?? '';
    });

    /**
     * When the parent clears `selectedFile` (e.g. after a successful upload),
     * also reset the native input's value so the field is empty and re-picking
     * the same filename still fires a change event.
     */
    private readonly resetNativeInputOnClear = effect(() => {
        if (this.selectedFileName()) {
            return;
        }
        const inputEl = this.fileInput()?.nativeElement;
        if (inputEl?.value) {
            inputEl.value = '';
        }
    });

    protected onFocusIn(): void {
        if (!this.disabled()) {
            this.focused.set(true);
        }
    }

    protected onFocusOut(event: FocusEvent): void {
        if (this.host.nativeElement.contains(event.relatedTarget as Node)) {
            return;
        }
        this.focused.set(false);
    }

    protected onBrowseClick(event: MouseEvent): void {
        event.stopPropagation();
        if (this.disabled()) {
            event.preventDefault();
            return;
        }
        this.openFileDialog();
    }

    protected onZoneClick(event: MouseEvent): void {
        if (this.disabled()) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        const inputEl = this.fileInput()?.nativeElement;
        if (inputEl) {
            inputEl.value = '';
        }
    }

    protected onFileInputChange(event: Event): void {
        if (this.disabled()) {
            return;
        }
        const inputEl = event.target as HTMLInputElement;
        const files = inputEl.files;
        if (!files?.length) {
            return;
        }
        if (this.multiple()) {
            Array.from(files).forEach((file) => this.fileSelected.emit(file));
        } else {
            this.fileSelected.emit(files[0]);
        }
    }

    protected onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (this.disabled()) {
            return;
        }
        if (!this.dragOver()) {
            this.dragOver.set(true);
            this.fileOver.emit(true);
        }
    }

    protected onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (!this.host.nativeElement.contains(event.relatedTarget as Node)) {
            this.dragOver.set(false);
            this.fileOver.emit(false);
        }
    }

    protected onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.dragOver.set(false);
        this.fileOver.emit(false);
        if (this.disabled()) {
            return;
        }
        const droppedFiles = event.dataTransfer?.files;
        if (!droppedFiles?.length) {
            return;
        }
        if (this.multiple()) {
            Array.from(droppedFiles).forEach((file) =>
                this.fileSelected.emit(file)
            );
        } else {
            this.fileSelected.emit(droppedFiles[0]);
        }
    }

    protected onClearClick(event: MouseEvent): void {
        event.stopPropagation();
        if (this.disabled()) {
            return;
        }
        const inputEl = this.fileInput()?.nativeElement;
        if (inputEl) {
            inputEl.value = '';
        }
        this.cleared.emit();
    }

    private openFileDialog(): void {
        const inputEl = this.fileInput()?.nativeElement;
        if (!inputEl || this.disabled()) {
            return;
        }
        inputEl.value = '';
        inputEl.click();
    }
}
